import {onBlockLoaded} from "@plaudit/frontend-apis";

import {getDismissedNotifications} from "./shared";

// Function to dismiss notification and set a cookie
function dismissNotification(target: HTMLElement, fromFormSubmit: boolean) {
	const notification = target.closest(".notification");
	if (!notification) {
		return;
	}

	const id = notification.getAttribute("data-notification-id");
	if (!id && id !== "0") {
		return; // We bail if the notification id is empty
	}

	// Hide the specific notification on the frontend
	notification.classList.remove("displayed");
	if (notification instanceof HTMLDialogElement) {
		// LastPass forces the scroll bars to remain on the dialog for some reason
		for (const lastPassRoot of notification.querySelectorAll("[data-lastpass-root]")) {
			lastPassRoot.remove();
		}

		if (fromFormSubmit) {
			setTimeout(() => notification.close(), parseFloat(notification.getAttribute("data-notification-close-after-form-submit-delay") ?? '5000'));
		} else {
			notification.close();
		}
	}

	if ((notification.getAttribute("data-notification-reopen-without-form-submit") === 'true') !== fromFormSubmit) {
		return;
	}

	const notificationInterval = notification.getAttribute('data-notification-interval');
	const hours = notificationInterval ? parseFloat(notificationInterval) : -1;

	// hours > 0:  number of hours to wait before showing again
	// hours = 0:  show again on next page load
	// hours = -1: never show again
	// Record that the notification was dismissed and when it should reappear if applicable
	if (hours !== 0) {
		const storage = window.localStorage.getItem("plaudit-dismissed-notifications");
		const expiration = hours > -1 ? Date.now() + hours * 60 * 60 * 1000 : 0;
		window.localStorage.setItem("plaudit-dismissed-notifications", storage ? `${storage};${id}:${expiration}` : `${id}:${expiration}`);
	}
}

// This code deliberately avoids using any helper libraries to allow them to continue to lazy-load
onBlockLoaded({
	blockOrClassName: "plaudit-notifications/renderer",
	onAdd(notificationsWrapper) {
		notificationsWrapper.addEventListener('click', e => {
			if (e.target instanceof HTMLElement && e.target.closest(".dismiss")) {
				dismissNotification(e.target, false);
			}
		});

		const dismissedNotifications = getDismissedNotifications();
		for (const notification of document.querySelectorAll('[data-notification-id]')) {
			// This is safe because the query selector already requires the attribute to be present
			const id = parseInt(notification.getAttribute('data-notification-id')!);
			if (id && !dismissedNotifications.includes(id)) {
				notification.classList.toggle("displayed", true);

				if (notification instanceof HTMLDialogElement) {
					if (notification.getAttribute("data-notification-exit-intent") === 'true') {
						const showDialog = () => {
							if (!getDismissedNotifications().includes(id)) {
								notification.showModal();
							}
						}

						let userTabbedAway = false;
						document.addEventListener('mouseout', e => {
							if (e.clientY < 0) {
								showDialog();
							}
						});

						document.addEventListener('visibilitychange', () => {
							if (document.visibilityState === 'hidden') {
								userTabbedAway = true;
							} else if (userTabbedAway) {
								userTabbedAway = false;
								showDialog();
							}
						});
					} else {
						notification.showModal();
					}
				}
			}
		}
	}
});

document.addEventListener('gform/theme/scripts_loaded', () => {
	(window as any).gform.utils.addAsyncFilter('gform/ajax/post_ajax_submission', async (data: {form: HTMLFormElement, submissionResult: Record<string, any>}) => {
		const {is_valid = false, submission_type = ''} = data.submissionResult?.data ?? {};
		if (is_valid && submission_type === 'submit') {
			dismissNotification(data.form, true);
		}
		return data;
	});
});
